import React, { useContext } from 'react';
import type { ChangeEvent, FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { Box, Grid, Stack, xcss } from '@atlaskit/primitives';
import { Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import Textfield from '@atlaskit/textfield';
import { useThemeObserver } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useBooleanFeatureFlag } from '@confluence/session-data';

import EntireWorkspaceGraphic from '../images/entireWorkspaceGraphic.png';
import EntireWorkspaceGraphicDark from '../images/entireWorkspaceGraphicDark.png';
import PageWithSubpagesGraphic from '../images/pageWithSubpagesGraphic.png';
import PageWithSubpagesGraphicDark from '../images/pageWithSubpagesGraphicDark.png';
import { PermissionsDropdown, PermissionOptions } from '../ImportContentDrawer/PermissionsDropdown';
import { ImportContentWorkflowContext } from '../ImportContentDrawer/ImportContentStore';

export const i18n = defineMessages({
	singularTitle: {
		id: 'import-content.set-up-spaces-step.singular-title',
		defaultMessage: 'Set up space',
		description:
			'Title of a step in a flow that will help the user set up a Confluence space from imported data',
	},
	pluralTitle: {
		id: 'import-content.set-up-spaces-step.plural-title',
		defaultMessage: 'Set up spaces',
		description:
			'Title of a step in a flow that will help the user set up a multiple Confluence spaces from imported data',
	},
	singularDescription: {
		id: 'import-content.set-up-spaces-step.singular-description',
		defaultMessage:
			'Name the new Confluence space we’ll import your content to and set the permissions to determine who can access it.',
		description:
			'Short description explaining to the user what they have to do to properly set up their space for their Notion content they are importing into Confluence',
	},
	pluralDescription: {
		id: 'import-content.set-up-spaces-step.plural-description',
		defaultMessage:
			'Each Notion teamspace will become a Confluence space. Set the permissions to determine who can access them.',
		description:
			'Short description explaining to the user what they have to do to properly set up their spaces for their Notion content they are importing into Confluence',
	},
	spaceNameLabel: {
		id: 'import-content.set-up-spaces-step.space-name-label',
		defaultMessage: 'Name your space',
		description:
			'The label above a text field where the user is naming their Confluence space that they are importing Notion data into',
	},
});

const getImageSrc = (hasSubpages: boolean, isDarkMode: boolean) => {
	if (hasSubpages) {
		return isDarkMode ? PageWithSubpagesGraphicDark : PageWithSubpagesGraphic;
	} else {
		return isDarkMode ? EntireWorkspaceGraphicDark : EntireWorkspaceGraphic;
	}
};

export const SetUpSpacesStep: FC = () => {
	const context = useContext(ImportContentWorkflowContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showNotionOption = useBooleanFeatureFlag(
		'confluence.frontend.show-notion-permissions-option',
	);
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	return (
		<Grid
			templateColumns="6fr 1fr 5fr"
			gap="space.200"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			xcss={xcss({ width: '800px' })}
		>
			<Stack space="space.200" alignInline="start">
				<Heading size="large">
					{context.hasSubpages ? (
						<FormattedMessage {...i18n.singularTitle} />
					) : (
						<FormattedMessage {...i18n.pluralTitle} />
					)}
				</Heading>
				<div>
					{context.hasSubpages ? (
						<FormattedMessage {...i18n.singularDescription} />
					) : (
						<FormattedMessage {...i18n.pluralDescription} />
					)}
				</div>

				{context.hasSubpages ? (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
					<Stack xcss={xcss({ width: '100%' })}>
						<Field
							name="Name your space"
							label={<FormattedMessage {...i18n.spaceNameLabel} />}
							isRequired
						>
							{({ fieldProps }) => (
								<Textfield
									{...fieldProps}
									value={context.spaceName}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										context.setSpaceName(event.target.value)
									}
								/>
							)}
						</Field>
					</Stack>
				) : null}

				<PermissionsDropdown
					shouldCopyPagePermissions={context.shouldCopyPagePermissions}
					supportedPermissions={
						showNotionOption && context.shouldCopyPagePermissions && context.shouldMapNotionMembers
							? [PermissionOptions.RESTRICTED, PermissionOptions.DEFAULT, PermissionOptions.NOTION]
							: [PermissionOptions.RESTRICTED, PermissionOptions.DEFAULT]
					}
					selectedPermissions={context.selectedPermissions}
					setSelectedPermissions={(selectedPermission: PermissionOptions) => {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'select',
								actionSubject: 'permissionsDropdown',
								source: 'importContentSetUpSpaces',
								attributes: {
									selectedPermission,
									contentType: 'Notion',
								},
							},
						}).fire();
						context.setSelectedPermissions(selectedPermission);
					}}
				/>
			</Stack>
			<Box />
			<Image src={getImageSrc(context.hasSubpages, isDarkMode)} />
		</Grid>
	);
};
